import { ReactComponent as IconChevronDown } from "@/assets/svgs/chevron-down.svg";
import { ReactComponent as IconChevronRight } from "@/assets/svgs/chevron-right.svg";
import { ReactComponent as IconNoMessage } from "@/assets/svgs/no-message.svg";
import { ReactComponent as TradeTrust } from "@/assets/svgs/tradetrust.svg";
import Button from "@/components/Button";
import { DATA_TABLE_PARENT } from "@/constants/index";
import useConfig from "@/hooks/use-config";
import useDataPage from "@/hooks/use-data-page/index";
import useMessages from "@/hooks/use-messages";
import getPageLabel from "@/hooks/use-page-details/index";
import useStaticData from "@/hooks/use-static-data/index";
import moment from "moment";
import SharedDataStatus from "../SharedDataStatus/index";
import { useFeature } from "@/hooks/use-feature/index";
import { features as featureConstant } from "@/utils/feature-verifier";

const DataTable = ({ itemData, type, parentComponentName, currentPage, goToNextPage, goToPrevPage, meta, handleCurrentInput, changeAllListCheckboxes, changeListCheckboxes, showModal, checkFileSize }) => {
	const isAgentPrincipalUIEnabled = useFeature(featureConstant.AGENT_PRINCIPAL_UI);
	const pageLimit = 10;
	// const { t } = useTranslation();
	const {
		goToTransaction,
		sortConfig,
		handleSort
	} = useDataPage();

	const staticData = useStaticData();
	const { config, isConsumer, isProvider } = useConfig();

	const { meta: messagesMeta } = useMessages();

	function getClassName(name) {
		if (!sortConfig) {
			return;
		}
		return sortConfig.key === name
			? sortConfig.direction === true
				? "is-asc"
				: "is-desc"
			: "";
	}

	const {
		getTypeName,
		isAllChecked,
	} = useDataPage();

	function displayType(item) {
		const type = item?.type;
		switch (parentComponentName) {
			case DATA_TABLE_PARENT.SHARED_DATA:
				if (isAgentPrincipalUIEnabled && type === 'PUSH' && item?.principals?.some(p => p.type === 'CONSUME_ON_BEHALF_OF_PRINCIPAL')) {
					return 'Push to Agent';
				}
				return getTypeName(type, "SHARED");
			case DATA_TABLE_PARENT.RECEIVED_DATA:
				if (isAgentPrincipalUIEnabled && type === 'PULL' && item?.principals?.some(p => p.type === 'PROVIDE_ON_BEHALF_OF_PRINCIPAL')) {
					return 'Pull from Agent';
				}
				return getTypeName(type, "RECIEVED");
			default:
				break;
		}
	}

	function viewDetailHandler(item) {
		switch (parentComponentName) {
			case DATA_TABLE_PARENT.SHARED_DATA:
				return goToTransaction(item.id, "provider");
			case DATA_TABLE_PARENT.RECEIVED_DATA:
				return goToTransaction(item.id, "consumer");
			default:
				break;
		}
	}

	function getCheckboxDisabledState(item) {
		switch (parentComponentName) {
			case DATA_TABLE_PARENT.SHARED_DATA:
				return item.status.toLowerCase() === "expired";
			case DATA_TABLE_PARENT.RECEIVED_DATA:
				return item.status.toLowerCase() !== "success";
			default:
				return false;
		}
	}

	function getTooltipText(item) {
		switch (parentComponentName) {
			case DATA_TABLE_PARENT.SHARED_DATA:
				return item?.isTTFlow && "TradeTrust Enabled";
			case DATA_TABLE_PARENT.RECEIVED_DATA:
				return (
					item?.verificationStatus !== "NOT APPLICABLE" &&
					"TradeTrust Transaction"
				);
			default:
				return false;
		}
	}

	function getCheckboxAllDisabledState() {
		switch (parentComponentName) {
			case DATA_TABLE_PARENT.SHARED_DATA:
				return !itemData.some(
					(item) =>
						item.status.toLowerCase() === "success" ||
						item.status.toLowerCase() === "pending" ||
						item.status.toLowerCase() === "fail"
				);
			case DATA_TABLE_PARENT.RECEIVED_DATA:
				return !itemData.some(
					(item) => item.status.toLowerCase() === "success"
				);
			default:
				return false;
		}
	}

	function getOrgIdPrefixText() {
		switch (parentComponentName) {
			case DATA_TABLE_PARENT.SHARED_DATA:
				return "Destination ";
			case DATA_TABLE_PARENT.RECEIVED_DATA:
				return "Sender ";
			default:
				return "";
		}
	}

	const list = itemData.map((item, index) => {
		let foundSystem;
		if (item?.type !== "STORE") {
			foundSystem = staticData?.systems?.find(
				(sys) => sys.systemId === item.sysId
			);
		}

		const foundOrg =
			item?.type === "STORE"
				? { name: "My Pitstop" }
				: staticData?.orgs?.find((org) => org.orgId === item.orgId) ?? {
					name: "-NA-",
				};

		const onBehalfOrg =
			item.on_behalf_of_id !== config?.identity?.organization?.id &&
			staticData?.orgs?.find((org) => org.orgId === item.on_behalf_of_id);

		const consumerOrgInfo = item.receivingType === "COPY" && staticData?.orgs?.find((org) => org.orgId === item.consumedOrgId)

		// Get PROVIDE_ON_BEHALF_OF_PRINCIPAL count for agent principal display
		const provideOnBehalfPrincipals = (isAgentPrincipalUIEnabled && item?.principals?.filter(
			p => p.type === 'PROVIDE_ON_BEHALF_OF_PRINCIPAL'
		)) || [];
		const hasProvideOnBehalfPrincipals = provideOnBehalfPrincipals.length > 0;

		// Get CONSUME_ON_BEHALF_OF_PRINCIPAL count for agent principal display
		const consumeOnBehalfPrincipals = (isAgentPrincipalUIEnabled && item?.principals?.filter(
			p => p.type === 'CONSUME_ON_BEHALF_OF_PRINCIPAL'
		)) || [];
		const hasConsumeOnBehalfPrincipals = consumeOnBehalfPrincipals.length > 0;

		return (
			<div className="data-table__table__body__row" key={index}>
				<div className="data-table__table__body__content data-table__table__body__content--checkbox">
					{
						(
							<label className="data-table__table__body__content--mc_checkbox">
								<input
									type="checkbox"
									name={item.label}
									id={item.id}
									value={item.id}
									checked={item.$selected}
									onChange={(e) => changeListCheckboxes(e, itemData)}
									disabled={getCheckboxDisabledState(item)}
								/>
								<span className="checkmark"></span>
							</label>
						)
					}
				</div>

				<div className={`data-table__table__body__content data-table__table__body__content--text data-table__table__body__content--heavy data-table__table__body__content--mc-data-element`}>
					<div className="data-table__data-element-info">
						<div className="data-table__data-element-name">
							{item.dataElement.name}
							{consumerOrgInfo && (
								<div className="data-table__table__body__content__proxy">Consumed by {consumerOrgInfo.name}</div>
							)}
							{isAgentPrincipalUIEnabled && <div>
								{(item.type === "PUSH" || item.type === "PROVIDE") && hasProvideOnBehalfPrincipals && (
									<div className="data-table__table__body__content__proxy data-table__table__body__content__proxy--agent">
										on behalf of {provideOnBehalfPrincipals.length} {provideOnBehalfPrincipals.length === 1 ? 'Principal' : 'Principals'}
									</div>
								)}
								{(item.type === "PULL" || item.type === "RECEIVE") && hasConsumeOnBehalfPrincipals && (
									<div className="data-table__table__body__content__proxy data-table__table__body__content__proxy--agent">
										on behalf of {consumeOnBehalfPrincipals.length} {consumeOnBehalfPrincipals.length === 1 ? 'Principal' : 'Principals'}
									</div>
								)}
							</div>}
							{item.receivingType !== "COPY" && onBehalfOrg && (
								<div className="data-table__table__body__content__proxy">
									on behalf of {onBehalfOrg.name}
								</div>
							)}
						</div>
						{item.receivingType === "COPY" && (
							<div className="data-table__table__body__content--copy_badge">
								<SharedDataStatus
									status={item.providerTransactionStatus === "FAIL" ? "COPY_FAILED" : "COPY_SUCCESS"}
									isReceivedData={parentComponentName === DATA_TABLE_PARENT.RECEIVED_DATA}
								/>
							</div>
						)}
					</div>
				</div>

				<div className={`data-table__table__body__content data-table__table__body__content--text data-table__table__body__content--mc-destination`}>
					{foundOrg.name}

					<span className="data-table__table__body__content__bottom">
						{foundSystem && (
							<span className="data-table__table__body__content__proxy">
								via {foundSystem.systemName}
							</span>
						)}
					</span>
				</div>
				<div className="data-table__table__body__content data-table__table__body__content--text">
					{moment(item.created).format("DD/MM/YYYY h:mma")}
				</div>
				{
					(
						<div className="data-table__table__body__content data-table__table__body__content--text data-table__table__body__content--flex data-table__table__body__content--mc_status">
							<SharedDataStatus
								status={item.status.toUpperCase()}
								isReceivedData={parentComponentName === DATA_TABLE_PARENT.RECEIVED_DATA}
							/>

							{getTooltipText(item) && (
								<div className="data-table__table__body__content__tradetrust-icon tooltip">
									<TradeTrust />
									<span className="tooltiptext">
										{getTooltipText(item)}
									</span>
								</div>
							)}
						</div>
					)
				}

				<div className="data-table__table__body__content data-table__table__body__content--text data-table__table__body__content--type">
					{displayType(item)}
				</div>
				<div className={`data-table__table__body__content data-table__table__body__content--text data-table__table__body__content--break data-table__table__body__content--mc-referenceId`}>
					{item.referenceId}
				</div>
				<div className={`data-table__table__body__content data-table__table__body__content--text data-table__table__body__content--mc-end`}>
					<span
						className="data-table__table__body__content__action link tooltip"
						onClick={() => viewDetailHandler(item)}
						id={parentComponentName === DATA_TABLE_PARENT.SHARED_DATA ? 'shared-data-view-details-id' : 'received-data-view-details-id'}
					>
						<span className="tooltiptext">View Details</span>
						<IconChevronRight />
					</span>
				</div>
			</div>
		);
	});

	const NoMessage = () => {
		return (
			<div className="data-table__empty__icon">
				<IconNoMessage />

				{parentComponentName === DATA_TABLE_PARENT.SHARED_DATA && !isProvider && (
					<span>
						Based on your configuration, you are not sharing any
						data elements
					</span>
				)}

				{parentComponentName === DATA_TABLE_PARENT.SHARED_DATA && isProvider && (
					<span>You have not share any data elements</span>
				)}

				{parentComponentName === DATA_TABLE_PARENT.RECEIVED_DATA && !isConsumer && (
					<span>
						Based on your configuration, you are not receiving any
						data elements
					</span>
				)}

				{parentComponentName === DATA_TABLE_PARENT.RECEIVED_DATA && isConsumer && (
					<span>You have not received any data elements</span>
				)}
			</div>
		);
	};

	return (
		<div className="data-table__content">
			<div className="data-table__content__cols">
				<div className="data-table__content__col">
					<div className="data-table__table">
						<div className="data-table__table__head">
							<div className="data-table__table__head__row">
								<div className="data-table__table__head__row__left"></div>
								<div
									className={
										itemData.filter(
											(message) =>
												message.$selected === true
										).length > 0
											? "data-table__table__head__row__right show-action-button"
											: "data-table__table__head__row__right hide-action-button"
									}
								>
									<Button
										text="Export"
										ghost
										submit={() => {
											checkFileSize(itemData);
											showModal();
										}}
									/>
								</div>
							</div>
							<div className="data-table__table__head__row">
								<div className="data-table__table__head__title data-table__table__head__title--checkbox">
									{
										<label className="data-table__table__body__content--mc_checkbox">
											<input
												type="checkbox"
												name="All"
												id="All"
												checked={isAllChecked(itemData)}
												onChange={(e) =>
													changeAllListCheckboxes(e, itemData)
												}
												disabled={getCheckboxAllDisabledState()}
											/>
											<span className="checkmark"></span>
										</label>
									}
								</div>
								<div
									className={`data-table__table__head__title data-table__table__head__title__sort-order link ${getClassName(
										"dataElementId"
									)} data-table__table__body__content--mc-data-element`}
									id="dataElementId"
									onClick={(_e) => {
										handleSort("dataElementId", type);
									}}
								>
									Data Element{" "}
									<span className="data-table__table__head__icon__sort-order"></span>
								</div>
								<div
									className={`data-table__table__head__title data-table__table__head__title__sort-order link ${getClassName(
										"orgId"
									)} data-table__table__head__title--mc-destination`}
									id="orgId"
									onClick={(_e) => {
										handleSort("orgId", type);
									}}
								>
									{getOrgIdPrefixText()}
									<span className="data-table__table__head__icon__sort-order"></span>
								</div>
								<div
									className={`data-table__table__head__title data-table__table__head__title__sort-order link ${getClassName(
										"created"
									)}`}
									id="created"
									onClick={(_e) => {
										handleSort("created", type);
									}}
								>
									Date/Time{" "}
									<span className="data-table__table__head__icon__sort-order"></span>
								</div>
								<div
									className={`data-table__table__head__title data-table__table__head__title__sort-order link ${getClassName(
										"messageStatus"
									)} data-table__table__head__title--mc_status`}
									id="messageStatus"
									onClick={(_e) => {
										handleSort("messageStatus", type);
									}}
								>
									Status{" "}
									<span className="data-table__table__head__icon__sort-order"></span>
								</div>
								<div
									className={`data-table__table__head__title data-table__table__head__title__sort-order link ${getClassName(
										"messageType"
									)} data-table__table__head__title__content--type`}
									id="messageType"
									onClick={(_e) => {
										handleSort("messageType", type);
									}}
								>
									Type{" "}
									<span className="data-table__table__head__icon__sort-order"></span>
								</div>
								<div
									className={`data-table__table__head__title data-table__table__head__title__sort-order link ${getClassName(
										"referenceId"
									)} data-table__table__head__title--mc-referenceId`}
									id="referenceId"
									onClick={(_e) => {
										handleSort("referenceId", type);
									}}
								>
									Reference ID{" "}
									<span className="data-table__table__head__icon__sort-order"></span>
								</div>
								<div className={`data-table__table__head__title data-table__table__head__title--mc-end `}>
									{/* Actions */}
								</div>
							</div>
						</div>
						<div className="data-table__table__body">
							{list.length > 0 ? (
								list
							) : (
								<div className="data-table__empty">
									<NoMessage />
								</div>
							)}
						</div>
						{list.length > 0 && (
							<div className="data-table__table__footer">
								<div className="data-table__table__footer__row">
									<div className="data-table__table__footer__row__left">
										<div className="data-table__table__pagination">
											<input
												className="data-table__table__pagination__page"
												type="number"
												value={currentPage}
												onChange={(e) =>
													handleCurrentInput(e)
												}
												min="1"
											/>
											<span className="data-table__table__pagination__page__meta">
												{" "}
												of{" "}
												{meta?.totalPages ||
													1}{" "}
												pages{" "}
											</span>
											<button
												className="data-table__table__pagination__prev"
												onClick={() => goToPrevPage()}
												disabled={currentPage === 1}
											>
												<IconChevronDown />
											</button>
											<button
												className="data-table__table__pagination__next"
												onClick={() => goToNextPage()}
												disabled={
													currentPage ===
													messagesMeta?.totalPages
												}
											>
												<IconChevronDown />
											</button>
										</div>
									</div>
									<div className="data-table__table__footer__row__right">
										<div className="data-table__table__meta">
											Showing{" "}
											{getPageLabel(
												meta?.total,
												pageLimit,
												currentPage - 1
											)}{" "}
											of {meta?.total || 0}{" "}
											entries
										</div>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default DataTable;
